import React from 'react';

import { ServiceType as ServiceTypeSlug } from '@rover/constants/src/translations/service.constants';
import type { ServiceBenefitsImageSrcSet } from '@rover/react-lib/src/pages/landing/components/ServiceBenefits';
import ServiceBenefits from '@rover/react-lib/src/pages/landing/components/ServiceBenefits';
import { AgeEnum } from '@rover/rsdk/src/apiClient/latest/model/ageEnum';
import type { LocalizedBrandServicePetType } from '@rover/rsdk/src/apiClient/latest/model/localizedBrandServicePetType';
import type { PetDetails } from '@rover/rsdk/src/apiClient/latest/model/petDetails';
import { PetTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/petTypeEnum';
import type { ServiceBenefitItem } from '@rover/rsdk/src/apiClient/latest/model/serviceBenefitItem';

import { PageSectionId, useSectionRef } from '../../SeoLocation/hooks/useSectionRef';

import dogTrainingImage from './images/dog-giving-paw.png';
import dogAndCatImage from './images/dog-scolding-cat.png';
import catImage from './images/focused-cat.png';
import dayCareImage from './images/happy-dog-grass.png';
import puppyImage from './images/puppy-in-the-grass.png';
import dogWalkingImage from './images/sleepy-chihuahua.png';
import boardingImage from './images/wasaaa-dog.png';
import { SERVICE_LANDING_SECTION_MARGIN_Y } from './ServiceLandingSection';

const makeImageSrcSet = (
  src: string,
  width: number,
  height: number
): ServiceBenefitsImageSrcSet => ({
  desktop: { src, width, height },
  mobile: { src, width, height },
});

const BOARDING_IMAGES = makeImageSrcSet(boardingImage, 480, 450);
const CAT_IMAGES = makeImageSrcSet(catImage, 480, 450);
const DOG_WALKING_IMAGES = makeImageSrcSet(dogWalkingImage, 480, 450);
const DOG_AND_CAT_IMAGES = makeImageSrcSet(dogAndCatImage, 480, 450);
const DAY_CARE_IMAGES = makeImageSrcSet(dayCareImage, 480, 450);
const DOG_TRAINING_IMAGES = makeImageSrcSet(dogTrainingImage, 480, 450);
const PUPPY_IMAGES = makeImageSrcSet(puppyImage, 480, 450);

type ServiceLandingBenefitsProps = {
  internalServiceTypeSlug: string;
  petType: LocalizedBrandServicePetType | undefined;
  age: PetDetails['age'];
  serviceBenefitItems: ServiceBenefitItem[];
  serviceBenefitsHeading: string;
};

// Mirrors getServiceLandingHeroImage: pet type/age come first for the two cases with a distinct
// look regardless of service type (cat, and puppy via the adolescent age band), then the image
// follows the internal service type.
export const getImages = (
  internalServiceTypeSlug: string,
  petType: LocalizedBrandServicePetType | undefined,
  age: PetDetails['age']
): ServiceBenefitsImageSrcSet => {
  if (petType === PetTypeEnum.cat) {
    return CAT_IMAGES;
  }
  if (petType === PetTypeEnum.dog && age === AgeEnum.adolescent) {
    return PUPPY_IMAGES;
  }

  switch (internalServiceTypeSlug) {
    case ServiceTypeSlug.OVERNIGHT_BOARDING:
      return BOARDING_IMAGES;
    case ServiceTypeSlug.DOG_WALKING:
      return DOG_WALKING_IMAGES;
    case ServiceTypeSlug.DROP_IN:
    case ServiceTypeSlug.OVERNIGHT_TRAVELING:
      return DOG_AND_CAT_IMAGES;
    case ServiceTypeSlug.DOGGY_DAY_CARE:
      return DAY_CARE_IMAGES;
    case ServiceTypeSlug.DOG_TRAINING:
      return DOG_TRAINING_IMAGES;
    default:
      return BOARDING_IMAGES; // default to a generic dog image for unknown service types
  }
};

const ServiceLandingBenefits = ({
  internalServiceTypeSlug,
  petType,
  age,
  serviceBenefitItems,
  serviceBenefitsHeading,
}: ServiceLandingBenefitsProps): JSX.Element | null => {
  const sectionRef = useSectionRef(PageSectionId.ServiceBenefits);

  if (serviceBenefitItems.length === 0) {
    return null;
  }

  return (
    <ServiceBenefits
      htmlRef={sectionRef}
      title={serviceBenefitsHeading}
      serviceBenefitsImageSrcSet={getImages(internalServiceTypeSlug, petType, age)}
      mobileImageGap="6x"
      marginY={SERVICE_LANDING_SECTION_MARGIN_Y}
    >
      <ServiceBenefits.List>
        {serviceBenefitItems.map((item) => (
          <ServiceBenefits.ListItem
            key={item.title}
            title={item.title}
            markerImage={
              item.image ? { url: item.image.url, altText: item.image.altText } : undefined
            }
          >
            {item.description}
          </ServiceBenefits.ListItem>
        ))}
      </ServiceBenefits.List>
    </ServiceBenefits>
  );
};

export default ServiceLandingBenefits;
